import { useCallback, useMemo } from 'react';
import { productData } from '../data/mockData';
import {
  dimsInInches as dimsInInchesFn,
  sizeMultiplierFromInches,
} from '../pages/QuoteCalculator/quoteMath';
import { resolveBackendCombo } from '../lib/pricingMap';
import { useBackendPrices } from './useBackendPrices';

/**
 * Quote pricing hook — owns the (size × qty × material) → price math
 * for the Quote Calculator wizard.
 *
 * Primary path: authoritative backend pricing (Excel-sourced). The
 * backend round-trips per-tier prices for every (combo, size, tier)
 * combo, which this hook prefetches and caches; `calculatePrice(qty)`
 * is synchronous after the prefetch lands.
 *
 * Fallback path: legacy local formula for (a) combos not yet mapped in
 * pricingMap.js, and (b) size-override "compare" quotes on Step 6.
 *
 * Returns:
 *   calculatePrice(qty, wInOverride?, hInOverride?) → number (USD)
 *   subtotal → memoised calculatePrice(quantity)
 *   backendCombo / backendPricingStatus / backendPriceExtrapolated /
 *   backendRequiresCustomQuote — surfaced so the Page 2 price-estimate
 *   banner can render the right hint.
 */
export const useQuotePricing = ({
  selectedProduct,
  selectedFormat,
  selectedMaterial,
  width,
  height,
  unit,
  quantity,
  isEvergreenTier,
  isIndividualPricing,
}) => {
  const backendCombo = useMemo(
    () => resolveBackendCombo(selectedProduct, selectedFormat, selectedMaterial),
    [selectedProduct, selectedFormat, selectedMaterial],
  );
  const { wIn: backendWIn, hIn: backendHIn } = useMemo(
    () => dimsInInchesFn(width, height, unit),
    [width, height, unit],
  );
  const backendTier = isEvergreenTier ? 'evergreen' : 'stickeryou';
  const {
    lookup: backendLookup,
    status: backendPricingStatus,
    extrapolated: backendPriceExtrapolated,
    requiresCustomQuote: backendRequiresCustomQuote,
  } = useBackendPrices({
    combo: backendCombo,
    widthIn: backendWIn,
    heightIn: backendHIn,
    tier: backendTier,
    enabled: !!backendCombo,
  });

  const calculatePrice = useCallback(
    (qty, wInOverride, hInOverride) => {
      if (!qty) return null;

      // Resolve the inches we'll price at: overrides (for "try another
      // size" quotes) win, or fall back to the active form state.
      // Inlined here (instead of via a closure) so useCallback's dep
      // array stays pinned to primitive state.
      const { wIn: stateWIn, hIn: stateHIn } = dimsInInchesFn(width, height, unit);
      const wIn = wInOverride ?? stateWIn;
      const hIn = hInOverride ?? stateHIn;

      // PRIMARY PATH — backend-sourced pricing (authoritative Excel).
      // Only valid when the user hasn't supplied a size override that
      // differs from the currently-prefetched combo size; overrides go
      // through the legacy formula below (fallback path).
      const sizeMatchesBackend =
        wInOverride === undefined && hInOverride === undefined;
      if (
        sizeMatchesBackend &&
        backendPricingStatus === 'ready' &&
        backendCombo
      ) {
        const cad = backendLookup(qty);
        if (cad !== null && cad > 0) {
          // Return in USD so the currency dropdown converts back to CAD
          // with the same multiplier as the rest of the price preview.
          return cad / 1.37;
        }
      }

      // FALLBACK PATH — legacy local calc for (a) combos not yet mapped
      // in pricingMap.js, (b) size-override "compare" quotes on Step 6.
      let priceInCAD; // PromotedYou prices are in CAD

      if (isIndividualPricing) {
        // Individual item pricing — use actual PromotedYou prices.
        const quantityTier = productData.quantitiesIndividual.find(
          (q) => q.qty === qty,
        );

        if (quantityTier && quantityTier.promotedYouPrice) {
          priceInCAD = quantityTier.promotedYouPrice;
        } else {
          // For custom quantities, interpolate or use highest tier.
          const sortedTiers = [...productData.quantitiesIndividual].sort(
            (a, b) => b.qty - a.qty,
          );
          const highestTier = sortedTiers[0];
          const lowestTier = sortedTiers[sortedTiers.length - 1];

          if (qty >= highestTier.qty) {
            const perUnit = highestTier.promotedYouPrice / highestTier.qty;
            priceInCAD = perUnit * qty;
          } else if (qty <= lowestTier.qty) {
            const perUnit = lowestTier.promotedYouPrice / lowestTier.qty;
            priceInCAD = perUnit * qty;
          } else {
            const lowerTier = sortedTiers.find((t) => t.qty <= qty);
            const upperTier = [...sortedTiers].reverse().find((t) => t.qty >= qty);

            if (lowerTier && upperTier) {
              const lowerPerUnit = lowerTier.promotedYouPrice / lowerTier.qty;
              const upperPerUnit = upperTier.promotedYouPrice / upperTier.qty;
              const avgPerUnit = (lowerPerUnit + upperPerUnit) / 2;
              priceInCAD = avgPerUnit * qty;
            } else {
              priceInCAD = lowestTier.promotedYouPrice;
            }
          }
        }
      } else {
        // Page-based pricing (original logic for stickers on pages).
        const basePrice = 20.38;
        let discount = 0;

        if (qty >= 10000) discount = 0.75;
        else if (qty >= 5000) discount = 0.75;
        else if (qty >= 3000) discount = 0.75;
        else if (qty >= 2500) discount = 0.74;
        else if (qty >= 2000) discount = 0.74;
        else if (qty >= 1500) discount = 0.71;
        else if (qty >= 1000) discount = 0.70;
        else if (qty >= 500) discount = 0.67;
        else if (qty >= 200) discount = 0.50;
        else if (qty >= 160) discount = 0.41;
        else if (qty >= 100) discount = 0.28;
        else if (qty >= 80) discount = 0.26;
        else if (qty >= 60) discount = 0.25;
        else if (qty >= 40) discount = 0.13;
        else discount = 0;

        // `qty` arriving here is ALREADY the page count for Page
        // products (the Step5 quantity picker now sends page counts,
        // not total stickers). Previously this line divided by
        // items-per-page which was a second, compounding division that
        // produced implausible "1 page for 1 sticker" prices.
        const pages = qty;
        const msrpPrice = basePrice * pages * (1 - discount);
        priceInCAD = msrpPrice * 1.37; // Convert USD to CAD
      }

      // Size-based multiplier for individual-pricing products.
      // Page-based products already account for size via items-per-page.
      if (isIndividualPricing) {
        priceInCAD *= sizeMultiplierFromInches(wIn, hIn);
      }

      // Two-tier pricing:
      //   - StickerYou tier (default for self sign-up / guests) = full
      //   - Evergreen tier (admin-created Strategic Partners)    = 30% off
      const tierMultiplier = isEvergreenTier ? 0.70 : 1.00;
      const ourPriceCAD = priceInCAD * tierMultiplier;

      // Return in USD (display converts for the selected currency).
      return ourPriceCAD / 1.37;
    },
    [
      width,
      height,
      unit,
      isEvergreenTier,
      isIndividualPricing,
      backendCombo,
      backendPricingStatus,
      backendLookup,
    ],
  );

  const subtotal = useMemo(() => calculatePrice(quantity), [calculatePrice, quantity]);

  return {
    calculatePrice,
    subtotal,
    backendCombo,
    backendPricingStatus,
    backendPriceExtrapolated,
    backendRequiresCustomQuote,
  };
};

export default useQuotePricing;
